/**
 * What a document may hold, as ProseMirror's schema (the core reads the same names:
 * `da-pages`): blocks (`title`, `heading` 1–3, `paragraph`, `blockquote`, lists, `table`,
 * `image` placed as Word places pictures (`pictures.ts`), `page_break`, `horizontal_rule`), inline `mention`s of other nodes and line breaks,
 * and marks (bold, italic, underline, struck, code, colour, highlight, font, size, comment).
 *
 * Every top-level block carries an `id` (kept while it is edited, so two people's saves merge
 * block by block) and `conflict` (`"theirs"`: the other person's version of the block before,
 * kept by a merge). Values that become CSS are checked first (`safeColour`, a known font, a
 * size in range): nothing typed reaches a style unchecked.
 */
import { Schema, type DOMOutputSpec, type MarkSpec, type Node as PMNode, type NodeSpec } from "prosemirror-model";
import { tableNodes } from "prosemirror-tables";
import { safeColour } from "../../shared/elements";
import { pictureCss, pictureLook, styleText } from "./pictures";
import { MAX_INDENT } from "./constants";

/** The fonts a document offers, by name, with the CSS each one is. */
export const DOC_FONTS: Record<string, string> = {
  Calibri: "Calibri, Carlito, 'Segoe UI', sans-serif",
  Arial: "Arial, Helvetica, 'Liberation Sans', sans-serif",
  Georgia: "Georgia, 'DejaVu Serif', serif",
  "Times New Roman": "'Times New Roman', Times, 'Liberation Serif', serif",
  Garamond: "Garamond, 'EB Garamond', Georgia, serif",
  "Courier New": "'Courier New', Courier, 'Liberation Mono', monospace",
  "Comic Sans": "'Comic Sans MS', 'Comic Neue', cursive",
};

/** The alignments a paragraph may take. */
export const ALIGNS = ["left", "center", "right", "justify"] as const;

/** A size in points, when it is one the editor accepts (6–96). */
export function safeSize(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n >= 6 && n <= 96 ? Math.round(n) : null;
}

/** The attributes every top-level block has. */
const BLOCK_ATTRS = { id: { default: null }, conflict: { default: null } };

/** The attributes of blocks of text: alignment and indent too. */
const TEXT_ATTRS = { ...BLOCK_ATTRS, align: { default: null }, indent: { default: 0 } };

/** A block's DOM attributes: its id, conflict mark, alignment and indent (checked). */
function textDom(node: PMNode, extra: Record<string, string> = {}): Record<string, string> {
  const style: string[] = [];
  const align = ALIGNS.find((a) => a === node.attrs.align);
  if (align) style.push(`text-align: ${align}`);
  const indent = Math.min(MAX_INDENT, Math.max(0, Number(node.attrs.indent) || 0));
  if (indent) style.push(`margin-left: ${indent * 1.27}cm`);
  const out: Record<string, string> = { ...extra };
  if (style.length) out.style = style.join("; ");
  if (node.attrs.id) out["data-block"] = String(node.attrs.id);
  if (node.attrs.conflict) out["data-conflict"] = "theirs";
  return out;
}

/** What a block of text's DOM says of its alignment and indent, read back. */
function textAttrsOf(dom: HTMLElement): Record<string, unknown> {
  const align = ALIGNS.find((a) => a === dom.style.textAlign) ?? null;
  return { align, indent: 0 };
}

/** The other DOM attributes of a top-level block. */
function blockDom(node: PMNode, extra: Record<string, string> = {}): Record<string, string> {
  const out = { ...extra };
  if (node.attrs.id) out["data-block"] = String(node.attrs.id);
  if (node.attrs.conflict) out["data-conflict"] = "theirs";
  return out;
}

const nodes: Record<string, NodeSpec> = {
  doc: { content: "block+" },
  title: {
    group: "block",
    content: "inline*",
    attrs: TEXT_ATTRS,
    defining: true,
    parseDOM: [{ tag: "h1.doc-title", priority: 60, getAttrs: textAttrsOf }],
    toDOM: (node): DOMOutputSpec => ["h1", textDom(node, { class: "doc-title" }), 0],
  },
  paragraph: {
    group: "block",
    content: "inline*",
    attrs: TEXT_ATTRS,
    parseDOM: [{ tag: "p", getAttrs: textAttrsOf }],
    toDOM: (node): DOMOutputSpec => ["p", textDom(node), 0],
  },
  heading: {
    group: "block",
    content: "inline*",
    attrs: { ...TEXT_ATTRS, level: { default: 1 } },
    defining: true,
    parseDOM: [1, 2, 3].map((level) => ({ tag: `h${level + 1}`, getAttrs: (dom: HTMLElement) => ({ ...textAttrsOf(dom), level }) })),
    toDOM: (node): DOMOutputSpec => [`h${Math.min(3, Math.max(1, Number(node.attrs.level) || 1)) + 1}`, textDom(node), 0],
  },
  blockquote: {
    group: "block",
    content: "(paragraph | bullet_list | ordered_list)+",
    attrs: BLOCK_ATTRS,
    defining: true,
    parseDOM: [{ tag: "blockquote" }],
    toDOM: (node): DOMOutputSpec => ["blockquote", blockDom(node), 0],
  },
  bullet_list: {
    group: "block",
    content: "list_item+",
    attrs: BLOCK_ATTRS,
    parseDOM: [{ tag: "ul" }],
    toDOM: (node): DOMOutputSpec => ["ul", blockDom(node), 0],
  },
  ordered_list: {
    group: "block",
    content: "list_item+",
    attrs: { ...BLOCK_ATTRS, order: { default: 1 } },
    parseDOM: [{ tag: "ol", getAttrs: (dom: HTMLElement) => ({ order: dom.hasAttribute("start") ? Number(dom.getAttribute("start")) || 1 : 1 }) }],
    toDOM: (node): DOMOutputSpec => ["ol", blockDom(node, node.attrs.order === 1 ? {} : { start: String(node.attrs.order) }), 0],
  },
  list_item: {
    content: "paragraph (paragraph | bullet_list | ordered_list)*",
    defining: true,
    parseDOM: [{ tag: "li" }],
    toDOM: (): DOMOutputSpec => ["li", 0],
  },
  image: {
    group: "block",
    atom: true,
    draggable: true,
    attrs: { ...BLOCK_ATTRS, src: { default: "" }, alt: { default: "" }, width: { default: 100 }, wrap: { default: "inline" }, align: { default: "center" }, x: { default: 0 }, y: { default: 0 } },
    parseDOM: [
      {
        tag: "figure[data-src]",
        getAttrs: (dom: HTMLElement) => ({
          src: dom.getAttribute("data-src") ?? "",
          alt: dom.getAttribute("data-alt") ?? "",
          ...pictureLook({ wrap: dom.dataset.wrap, align: dom.dataset.align, width: dom.dataset.width, x: dom.dataset.x, y: dom.dataset.y }),
        }),
      },
    ],
    toDOM: (node): DOMOutputSpec => {
      const look = pictureLook(node.attrs);
      const data = { "data-src": String(node.attrs.src), "data-alt": String(node.attrs.alt), "data-wrap": look.wrap, "data-align": look.align, "data-width": String(look.width), "data-x": String(look.x), "data-y": String(look.y) };
      return ["figure", blockDom(node, { class: "doc-image", ...data, style: styleText(pictureCss(look).figure) })];
    },
  },
  page_break: {
    group: "block",
    atom: true,
    attrs: BLOCK_ATTRS,
    parseDOM: [{ tag: "div.doc-page-break" }],
    toDOM: (node): DOMOutputSpec => ["div", blockDom(node, { class: "doc-page-break" })],
  },
  horizontal_rule: {
    group: "block",
    attrs: BLOCK_ATTRS,
    parseDOM: [{ tag: "hr" }],
    toDOM: (node): DOMOutputSpec => ["hr", blockDom(node)],
  },
  text: { group: "inline" },
  hard_break: {
    inline: true,
    group: "inline",
    selectable: false,
    parseDOM: [{ tag: "br" }],
    toDOM: (): DOMOutputSpec => ["br"],
  },
  mention: {
    inline: true,
    group: "inline",
    atom: true,
    attrs: { name: { default: "" }, id: { default: null } },
    parseDOM: [{ tag: "span[data-mention]", getAttrs: (dom: HTMLElement) => ({ name: dom.getAttribute("data-mention") ?? "", id: dom.getAttribute("data-mention-id") }) }],
    toDOM: (node): DOMOutputSpec => ["span", { class: "doc-mention", "data-mention": String(node.attrs.name), ...(node.attrs.id ? { "data-mention-id": String(node.attrs.id) } : {}) }, String(node.attrs.name)],
    leafText: (node) => String(node.attrs.name),
  },
};

// Tables, from prosemirror-tables, the table itself a top-level block with an id.
const tables = tableNodes({ tableGroup: "block", cellContent: "(paragraph | bullet_list | ordered_list)+", cellAttributes: {} });
nodes.table = { ...tables.table, attrs: { ...(tables.table.attrs ?? {}), ...BLOCK_ATTRS }, toDOM: (node): DOMOutputSpec => ["table", blockDom(node), ["tbody", 0]] };
nodes.table_row = tables.table_row;
nodes.table_cell = tables.table_cell;
nodes.table_header = tables.table_header;

const marks: Record<string, MarkSpec> = {
  strong: { parseDOM: [{ tag: "strong" }, { tag: "b" }], toDOM: () => ["strong", 0] },
  em: { parseDOM: [{ tag: "em" }, { tag: "i" }], toDOM: () => ["em", 0] },
  underline: { parseDOM: [{ tag: "u" }], toDOM: () => ["u", 0] },
  strike: { parseDOM: [{ tag: "s" }], toDOM: () => ["s", 0] },
  code: { parseDOM: [{ tag: "code" }], toDOM: () => ["code", 0] },
  color: {
    attrs: { color: { default: "" } },
    parseDOM: [{ tag: "span[data-color]", getAttrs: (dom: HTMLElement) => ({ color: dom.getAttribute("data-color") ?? "" }) }],
    toDOM: (mark) => {
      const c = safeColour(String(mark.attrs.color));
      return ["span", c ? { "data-color": c, style: `color: ${c}` } : {}, 0];
    },
  },
  highlight: {
    attrs: { color: { default: "#fff59d" } },
    parseDOM: [{ tag: "mark", getAttrs: (dom: HTMLElement) => ({ color: dom.getAttribute("data-color") ?? "#fff59d" }) }],
    toDOM: (mark) => {
      const c = safeColour(String(mark.attrs.color));
      return ["mark", c ? { "data-color": c, style: `background: ${c}` } : {}, 0];
    },
  },
  font: {
    attrs: { family: { default: "" } },
    parseDOM: [{ tag: "span[data-font]", getAttrs: (dom: HTMLElement) => ({ family: dom.getAttribute("data-font") ?? "" }) }],
    toDOM: (mark) => {
      const family = DOC_FONTS[String(mark.attrs.family)];
      return ["span", family ? { "data-font": String(mark.attrs.family), style: `font-family: ${family}` } : {}, 0];
    },
  },
  size: {
    attrs: { size: { default: 11 } },
    parseDOM: [{ tag: "span[data-size]", getAttrs: (dom: HTMLElement) => ({ size: Number(dom.getAttribute("data-size")) || 11 }) }],
    toDOM: (mark) => {
      const size = safeSize(mark.attrs.size);
      return ["span", size ? { "data-size": String(size), style: `font-size: ${size}pt` } : {}, 0];
    },
  },
  comment: {
    attrs: { id: { default: "" }, text: { default: "" }, by: { default: "" } },
    inclusive: false,
    excludes: "",
    parseDOM: [{ tag: "span[data-comment]", getAttrs: (dom: HTMLElement) => ({ id: dom.getAttribute("data-comment") ?? "", text: dom.getAttribute("title") ?? "", by: dom.getAttribute("data-by") ?? "" }) }],
    toDOM: (mark) => ["span", { class: "doc-comment", "data-comment": String(mark.attrs.id), title: `${mark.attrs.by ? `${mark.attrs.by}: ` : ""}${mark.attrs.text}`, "data-by": String(mark.attrs.by) }, 0],
  },
};

/** The documents' schema. */
export const docSchema = new Schema({ nodes, marks });

